import { useMutation } from '@tanstack/react-query'
import { useQueryCallbacks } from 'core/hooks/data/useQueryCallbacks'
import { downloadFile } from 'utils/AppHelper'
import APIService from 'api/APIService'
import { JobStatus, PayPeriod } from 'types/entity'
import Button from 'core/components/buttons/Button'
import { useSettings } from 'core/hooks/settings/useSettings'
import useNotifications from 'core/hooks/ui/useNotifications'

const usePeriodsFundingRequest = (onSuccess?: VoidFunction) => {
  const { data: settings, set } = useSettings()
  const notification = useNotifications()

  const jobID = settings.pollingJobs.fundingRequestJobId

  const {
    mutate: fundingRequest,
    status: fundingRequestStatus,
    isPending: downloadingFundingRequest
  } = useMutation({
    mutationFn: (periods: PayPeriod['id'][]) => {
      return APIService.payPeriods.fundingRequestDownload(periods)
    },
    onSuccess: (data) => {
      set('pollingJobs', {
        ...settings.pollingJobs,
        fundingRequestJobId: data.token_id
      })
      onSuccess?.()
    },
    onError: () => {
      notification.error('An error has occurred trying to download this file')
    }
  })

  const loadingNotificationId = `funding-request-${jobID}`

  useQueryCallbacks({
    queryKey: ['download-summary', jobID],
    queryFn: () =>
      APIService.misc.getJobStatus({
        jobId: jobID
      }),
    enabled: !!jobID,
    refetchInterval: 2000,
    onSuccess: (response: JobStatus) => {
      const errors = response?.error_list ?? []
      const hasErrors = errors.length > 0
      if (response.status === 'finished' && !hasErrors) {
        set('pollingJobs', {
          ...settings.pollingJobs,
          fundingRequestJobId: ''
        })

        downloadFile(response.data.file_url as string)
        notification.success({
          title: 'Your download is complete.'
        })
        notification.dismiss(loadingNotificationId)
      }

      if (
        (response.status === 'in_progress' || response.status === 'enqueued') &&
        !hasErrors
      ) {
        notification.loading({
          id: loadingNotificationId,
          position: 'bottom-left',
          title: 'Downloading funding request...',
          subtitle: 'Downloading funding request, this may take a few minutes.',
          duration: Infinity
        })
      }

      if (
        response.status === 'failed' ||
        (response.status === 'finished' && hasErrors)
      ) {
        notification.dismiss(loadingNotificationId)
        notification.error({
          title: 'There were some errors with your file',
          subtitle: (
            <div>
              {typeof response.data?.message === 'string' &&
                response.data.message?.split(';').map((error) => {
                  return <span key={error}>{error.replace(';', '')}</span>
                })}
            </div>
          ),
          id: jobID,
          footer: () => {
            return (
              <Button
                className="text-primary"
                variant="link"
                onClick={() => {
                  if (response.data.file_url)
                    downloadFile(response.data.file_url as string)
                }}
              >
                Download
              </Button>
            )
          }
        })
        set('pollingJobs', {
          ...settings.pollingJobs,
          fundingRequestJobId: ''
        })
      }
    },
    onError: () => {
      notification.dismiss(loadingNotificationId)
      notification.error({
        title: 'Could not download funding request',
        id: jobID
      })
      set('pollingJobs', {
        ...settings.pollingJobs,
        fundingRequestJobId: ''
      })
    }
  })

  return {
    fundingRequest,
    fundingRequestStatus,
    downloadingFundingRequest
  }
}

export default usePeriodsFundingRequest
